import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserPlus, Mail, Lock, User, AlertCircle, CheckCircle, BookOpen, GraduationCap, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const inviteToken = searchParams.get('invite');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCourse, setInviteCourse] = useState(null);
  const [inviteTeacher, setInviteTeacher] = useState(null);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (!inviteToken) {
      setInviteCourse(null);
      setInviteTeacher(null);
      setInviteError('');
      return;
    }

    let active = true;
    axios
      .get(`${API_BASE_URL}/invite/${inviteToken}`)
      .then((res) => {
        if (!active) return;
        if (res.data.success) {
          setInviteCourse(res.data.course);
          setInviteTeacher(res.data.teacher || null);
          setInviteError('');
        } else {
          setInviteError('Il link di invito non è valido o è scaduto.');
        }
      })
      .catch(() => {
        if (active) setInviteError('Il link di invito non è valido o è scaduto.');
      });

    return () => { active = false; };
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.password_confirmation) {
      setError('Le password non coincidono');
      return;
    }

    if (formData.password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }

    setLoading(true);

    try {
      const enrolledCourse = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.password_confirmation,
        inviteToken || null,
      );

      if (enrolledCourse?.id) {
        navigate(`/dashboard/${enrolledCourse.id}`);
      } else {
        navigate('/corsi');
      }
    } catch (err) {
      setError(err.message || 'Errore durante la registrazione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return null;

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: 'Debole', color: '#ef4444', width: '33%' };
    if (strength <= 3) return { level: 'Media', color: '#f59e0b', width: '66%' };
    return { level: 'Forte', color: '#2cb39a', width: '100%' };
  };

  const inputFocusStyle = { borderColor: '#2cb39a', background: '#fff', boxShadow: '0 0 0 3px rgba(44,179,154,0.12)' };
  const inputBlurStyle = { borderColor: '#e2e8f0', background: '#f8fafc', boxShadow: 'none' };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #f0faf8 0%, #e8f7f4 40%, #ffffff 100%)' }}>

      {/* Decorative background circles */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #2cb39a 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #2cb39a 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

      <div className="w-full max-w-md relative z-10 my-6">
        {/* Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-white shadow-2xl p-8"
          style={{ boxShadow: '0 25px 60px rgba(44, 179, 154, 0.12), 0 8px 24px rgba(0,0,0,0.06)' }}>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-2 flex items-center justify-center">
              <img src="/favicon.png" alt="ALMA" className="w-full h-full object-contain" onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }} />
              <Sparkles className="w-8 h-8 text-[#2cb39a] hidden" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Crea il tuo account</h1>
            <p className="text-sm text-slate-500">
              {inviteToken ? 'Registrati e iscriviti al corso' : 'Inizia la tua esperienza con ALMA'}
            </p>
          </div>

          {/* Invite banner */}
          {inviteToken && !inviteError && (
            <div className="mb-6 p-4 rounded-2xl border flex items-start gap-3"
              style={{ background: 'linear-gradient(135deg, #f0faf8, #e8f7f4)', borderColor: '#ace5d9' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #2cb39a, #239a84)' }}>
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                {inviteCourse ? (
                  <>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#239a84' }}>
                      Invito al corso
                    </p>
                    <p className="text-sm font-bold text-slate-800 leading-tight truncate">{inviteCourse.title}</p>
                    {inviteTeacher?.name && (
                      <div className="mt-1.5 flex items-center gap-1.5" style={{ color: '#2cb39a' }}>
                        <GraduationCap className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-xs font-medium truncate">Docente: {inviteTeacher.name}</span>
                      </div>
                    )}
                    <p className="text-xs mt-1.5 text-slate-500 italic">
                      Sarai iscritto automaticamente al corso al termine della registrazione.
                    </p>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#2cb39a', borderTopColor: 'transparent' }} />
                    <p className="text-sm font-medium" style={{ color: '#2cb39a' }}>Verifica del link...</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {(inviteError || error) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-medium">{inviteError || error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">Nome completo</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <User style={{ width: '1.1rem', height: '1.1rem', color: '#94a3b8' }} />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="block w-full pl-10 pr-4 py-3 rounded-xl text-slate-800 text-sm transition-all duration-200 outline-none"
                  style={{ border: '1.5px solid #e2e8f0', background: '#f8fafc' }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                  placeholder="Mario Rossi"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail style={{ width: '1.1rem', height: '1.1rem', color: '#94a3b8' }} />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full pl-10 pr-4 py-3 rounded-xl text-slate-800 text-sm transition-all duration-200 outline-none"
                  style={{ border: '1.5px solid #e2e8f0', background: '#f8fafc' }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                  placeholder="nome@esempio.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock style={{ width: '1.1rem', height: '1.1rem', color: '#94a3b8' }} />
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="block w-full pl-10 pr-4 py-3 rounded-xl text-slate-800 text-sm transition-all duration-200 outline-none"
                  style={{ border: '1.5px solid #e2e8f0', background: '#f8fafc' }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                  placeholder="Min. 8 caratteri"
                />
              </div>
              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">Sicurezza:</span>
                    <span className="text-xs font-bold" style={{ color: passwordStrength.color }}>{passwordStrength.level}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all duration-300"
                      style={{ width: passwordStrength.width, background: passwordStrength.color }} />
                  </div>
                </div>
              )}
            </div>

            {/* Password Confirm */}
            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-semibold text-slate-700 mb-1.5">Conferma Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock style={{ width: '1.1rem', height: '1.1rem', color: '#94a3b8' }} />
                </div>
                <input
                  type="password"
                  id="password_confirmation"
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                  className="block w-full pl-10 pr-10 py-3 rounded-xl text-slate-800 text-sm transition-all duration-200 outline-none"
                  style={{ border: '1.5px solid #e2e8f0', background: '#f8fafc' }}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                  placeholder="Ripeti la password"
                />
                {formData.password_confirmation && (
                  <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center">
                    {formData.password === formData.password_confirmation
                      ? <CheckCircle style={{ width: '1.1rem', height: '1.1rem', color: '#2cb39a' }} />
                      : <AlertCircle style={{ width: '1.1rem', height: '1.1rem', color: '#ef4444' }} />
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: loading ? '#86c9bc' : 'linear-gradient(135deg, #2cb39a, #239a84)',
                boxShadow: loading ? 'none' : '0 4px 15px rgba(44,179,154,0.4)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => { if (!loading) { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 8px 20px rgba(44,179,154,0.45)'; }}}
              onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = loading ? 'none' : '0 4px 15px rgba(44,179,154,0.4)'; }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Registrazione in corso...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  <span>{inviteCourse ? `Registrati e iscriviti al corso` : 'Crea Account'}</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Hai già un account?{' '}
              <Link
                to={inviteToken ? `/login?invite=${inviteToken}` : '/login'}
                className="font-bold transition-colors"
                style={{ color: '#2cb39a' }}
                onMouseEnter={(e) => { e.target.style.color = '#239a84'; }}
                onMouseLeave={(e) => { e.target.style.color = '#2cb39a'; }}
              >
                Accedi
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} ALMA · Piattaforma di Apprendimento AI
        </p>
      </div>
    </div>
  );
};

export default Register;
